import { ref, markRaw } from 'vue';

// Cross-filter context keyed on the physical asset (assetKey), not the RDrive
// item. Every visual registers "which assets are in scope" as a SQL query
// returning assetKey; the intersection of all registered filters lands in the
// filter_context temp table, and the two views below derive both grains from
// it:
//
//   filteredAssets  unique_assets in scope  (+ today-relative due columns)
//   filteredItems   every lifecycle record of an asset in scope
//
// `filtersLastUpdated` is the single reactive handle - the context itself is
// markRaw so Vue never proxies the DuckDB objects.
export class FilterAssetsContext {
  constructor(duckDBService) {
    markRaw(this);
    this.duckDBService = markRaw(duckDBService);
    this.filterQueries = markRaw({});   // visualId -> SQL
    this.filters = markRaw({});         // visualId -> match count
    this.filterLabels = markRaw({});    // visualId -> human label for the chips
    this.filtersLastUpdated = ref(new Date());
    this._chain = Promise.resolve();
  }

  // Filter mutations are serialised: a rapid pair of clicks would otherwise
  // interleave the DELETE/INSERT pairs on filter_context.
  _enqueue(task) {
    const run = this._chain.then(task, task);
    this._chain = run.catch(() => {});
    return run;
  }

  async initializeFilterContext() {
    const conn = this.duckDBService.conn;

    // Drop filter views left over from a previous load; they may reference
    // columns that no longer exist.
    for (const visualId of Object.keys(this.filters)) {
      await conn.queryRaw(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        DROP TABLE IF EXISTS filter_table_${visualId};
      `);
    }

    await conn.queryRaw(`
      DROP VIEW IF EXISTS filteredAssets;
      DROP VIEW IF EXISTS filteredItems;
      DROP TABLE IF EXISTS filter_context;

      CREATE TEMPORARY TABLE filter_context(assetKey VARCHAR);
      CREATE UNIQUE INDEX filter_context_idx ON filter_context(assetKey);

      INSERT INTO filter_context
      SELECT assetKey FROM unique_assets;

      CREATE VIEW filteredAssets AS
      SELECT
        u.*,
        date_diff('day', CURRENT_DATE, u.nextInspectionDate) AS daysToNextInspection,
        COALESCE(u.nextInspectionDate < CURRENT_DATE AND NOT u.inspectedSinceDue, FALSE) AS isOverdue,
        CASE
          WHEN u.nextInspectionDate IS NULL OR u.inspectedSinceDue THEN 'none'
          WHEN u.nextInspectionDate < CURRENT_DATE THEN 'overdue'
          WHEN u.nextInspectionDate <= CURRENT_DATE + INTERVAL 30 DAY THEN 'due30'
          WHEN u.nextInspectionDate <= CURRENT_DATE + INTERVAL 60 DAY THEN 'due60'
          ELSE 'later'
        END AS dueBucket,
        u.inspectionCount = 0 AS neverInspected,
        COALESCE(u.installDate <= CURRENT_DATE, FALSE) AS isInstalled
      FROM unique_assets u
      INNER JOIN filter_context fc USING (assetKey);

      CREATE VIEW filteredItems AS
      SELECT ai.*
      FROM asset_items ai
      INNER JOIN filter_context fc USING (assetKey);
    `);

    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filterLabels = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  // Register (or replace) a visual's filter from a query returning assetKey.
  // Filter queries read the UNFILTERED unique_assets / asset_items tables so a
  // visual can always widen its own selection.
  updateFilterWithQuery(visualId, query, label = null) {
    return this._enqueue(async () => {
      const conn = this.duckDBService.conn;
      try {
        await conn.query(`
          DROP VIEW IF EXISTS filter_view_${visualId};
          CREATE TEMPORARY VIEW filter_view_${visualId} AS (
            SELECT DISTINCT assetKey FROM (${query})
          );
        `);

        const countResult = await conn.query(`SELECT COUNT(*) AS count FROM filter_view_${visualId}`);
        const countRow = countResult.toArray()[0];
        this.filters[visualId] = countRow ? Number(countRow.count) : 0;
        this.filterQueries[visualId] = query;
        this.filterLabels[visualId] = label;

        await this._applyFilters();
      } catch (error) {
        console.error(`Error updating filter "${visualId}":`, error);
      }
    });
  }

  // Convenience: a WHERE condition over unique_assets.
  updateFilterWithCondition(visualId, condition, label = null) {
    return this.updateFilterWithQuery(visualId, `SELECT assetKey FROM unique_assets WHERE ${condition}`, label);
  }

  // Legacy path: an explicit list of asset keys.
  updateFilter(visualId, assetKeys, label = null) {
    return this._enqueue(async () => {
      const conn = this.duckDBService.conn;
      const filterTable = `filter_table_${visualId}`;

      await conn.query(`DROP TABLE IF EXISTS ${filterTable}`);
      await conn.query(`CREATE TEMPORARY TABLE ${filterTable}(assetKey VARCHAR)`);

      const batchSize = 200000;
      for (let i = 0; i < assetKeys.length; i += batchSize) {
        const chunk = assetKeys.slice(i, i + batchSize)
          .map((value) => `('${String(value).replace(/'/g, "''")}')`)
          .join(',');
        if (chunk) {
          await conn.query(`INSERT INTO ${filterTable} VALUES ${chunk}`);
        }
      }

      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS
        SELECT DISTINCT assetKey FROM ${filterTable};
      `);

      this.filters[visualId] = assetKeys.length;
      this.filterLabels[visualId] = label;
      await this._applyFilters();
    });
  }

  removeFilter(visualId) {
    return this._enqueue(async () => {
      if (this.filters[visualId] === undefined) return;

      const conn = this.duckDBService.conn;
      await conn.query(`DROP VIEW IF EXISTS filter_view_${visualId}`);
      await conn.query(`DROP TABLE IF EXISTS filter_table_${visualId}`);

      delete this.filters[visualId];
      delete this.filterQueries[visualId];
      delete this.filterLabels[visualId];

      if (Object.keys(this.filters).length === 0) {
        await this._reset();
      } else {
        await this._applyFilters();
      }
    });
  }

  resetFilters() {
    return this._enqueue(async () => {
      const conn = this.duckDBService.conn;
      for (const visualId of Object.keys(this.filters)) {
        await conn.query(`DROP VIEW IF EXISTS filter_view_${visualId}`);
        await conn.query(`DROP TABLE IF EXISTS filter_table_${visualId}`);
      }
      await this._reset();
    });
  }

  async _reset() {
    const conn = this.duckDBService.conn;
    await conn.query(`
      DELETE FROM filter_context;
      INSERT INTO filter_context
      SELECT assetKey FROM unique_assets;
    `);
    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filterLabels = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  // Intersect every registered filter view into filter_context.
  async _applyFilters() {
    const conn = this.duckDBService.conn;
    const visualIds = Object.keys(this.filters);

    if (visualIds.length === 0) {
      await this._reset();
      return;
    }

    try {
      const intersection = visualIds
        .map((visualId) => `SELECT assetKey FROM filter_view_${visualId}`)
        .join(' INTERSECT ');

      await conn.query(`
        DELETE FROM filter_context;
        INSERT INTO filter_context
        ${intersection};
      `);

      this.filtersLastUpdated.value = new Date();
    } catch (error) {
      console.error('Error applying filters:', error);
      await this._reset();
    }
  }

  get activeFilterCount() {
    return Object.keys(this.filters).length;
  }

  // Snapshot for the Active Filters bar (the maps themselves are markRaw).
  getActiveFilters() {
    return Object.keys(this.filters).map((visualId) => ({
      visualId,
      count: this.filters[visualId],
      label: this.filterLabels[visualId] || null,
    }));
  }

  async getFilteredIds() {
    const conn = this.duckDBService.conn;
    try {
      const res = await conn.query('SELECT assetKey FROM filter_context');
      if (res?.getColumn) return res.getColumn('assetKey').toArray();
      if (Array.isArray(res)) return res.map((row) => row.assetKey);
      return [];
    } catch (error) {
      console.error('Error getting filtered asset keys:', error);
      return [];
    }
  }

  async executeFilteredQuery(sql) {
    const conn = this.duckDBService.conn;
    const ts = this.filtersLastUpdated.value.getTime();
    try {
      return await conn.query(`
        SELECT q.*
        FROM (${sql}) AS q
        INNER JOIN filter_context fc USING (assetKey)
        /* ${ts} */
      `);
    } catch (error) {
      console.error('Error executing filtered query:', error);
      throw error;
    }
  }

  getFilterDebugInfo() {
    return {
      activeFilters: Object.keys(this.filters).length,
      filtersByVisual: Object.entries(this.filters).map(([visualId, count]) => ({
        visualId,
        matchCount: count,
        label: this.filterLabels[visualId] || null,
        hasQuery: !!this.filterQueries[visualId],
      })),
      lastUpdated: this.filtersLastUpdated.value,
    };
  }
}
